import { useQuery } from '@tanstack/react-query';
import {
  Assignee,
  OverviewDashboardStatus,
  States,
} from 'infrastructure/enums';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import dataService from 'services/dataService';
import { useEffect } from 'react';
import { showResult } from 'infrastructure/hooks/utils';
import { useUpdateAssigneeOverviewDashboard } from 'infrastructure/hooks/assign-patient';
import { mainlandTimezonesMapping } from 'infrastructure/data-sources/timezones';
import { multiStateLicenseStates } from 'infrastructure/data-sources/states';

import type {
  ClinicDoNotCallRestrictionType,
  CroStatuses,
  DoNotCallRestrictionType,
  Languages,
  OverviewDashboardSortEnum,
  Timezones,
} from 'infrastructure/enums';
import type { Nullable } from 'infrastructure/types';
import type { TOverviewDashboardFilter } from './use-filter';
import type {
  IAssignee,
  IPartialPaginationMeta,
} from 'infrastructure/interfaces';

export interface RpmOverviewPatient {
  name: string;
  guid: string;
  dob: string;
  language: Languages;
  provider: string;
  readings: {
    normal: number;
    atRisk: number;
    critical: number;
  };
  assignee?: IAssignee;
  overTime?: boolean;
  croStatus?: CroStatuses | null;
  interactionStatus?: boolean;
  doNotCallRestrictionType?: Nullable<DoNotCallRestrictionType>;
  doNotCallStartTime?: Nullable<string>;
  doNotCallEndTime?: Nullable<string>;
  clinicDoNotCallRestrictionType?: Nullable<ClinicDoNotCallRestrictionType>;
  clinicDoNotCallStartTime?: Nullable<string>;
  clinicDoNotCallEndTime?: Nullable<string>;
  timezone?: Nullable<string>;
  actionedByAgencyUser?: boolean;
  slaDeadline?: number;
  state?: States | null;
}

export interface RpmOverviewDashboardResponse {
  data: RpmOverviewPatient[];
  count: number;
}

export type TDataFilterWithPaginationMeta = TOverviewDashboardFilter &
  IPartialPaginationMeta;

export type TDataFilter = Omit<
  TDataFilterWithPaginationMeta,
  'doctorGuid' | 'startTime' | 'endTime' | 'status' | 'timezone'
> & {
  childClinic?: boolean;
  latestReadingStartTime?: number;
  latestReadingEndTime?: number;
  year?: number;
  month?: number;
  readingStatus?: OverviewDashboardStatus;
  latestReading: OverviewDashboardSortEnum;
  priorityPatients?: boolean;
  timezone?: Timezones[];
};

export const PAGE_SIZE = 100;

const url = 'reports/reports/interactive-time-dashboard-overview';

export const getRpmOverviewDashboardQueryKey = (filter: TDataFilter) => [
  url,
  filter,
];

const queryFn = async (filter: TDataFilter) => {
  const newFilters = {
    ...filter,
  };
  if (filter.readingStatus === OverviewDashboardStatus.Priority) {
    newFilters.readingStatus = undefined;
    newFilters.priorityPatients = true;
  }

  const { data, error } =
    await dataService.getList<RpmOverviewDashboardResponse>(
      apiUrlGenerator(url, newFilters),
    );

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

export const useRpmOverviewDashboard = (
  filter: TDataFilterWithPaginationMeta,
) => {
  const { startTime, endTime, status, ...filterWithoutDates } = filter;
  const filterWithItems: TDataFilter = {
    ...filterWithoutDates,
    assignee: filter.assignee === Assignee.All ? undefined : filter.assignee,
    items: PAGE_SIZE,
    clinicGuid: filter.parentClinicGuid || filter.clinicGuid,
    childClinic: Boolean(filter.parentClinicGuid) || undefined,
    parentClinicGuid: undefined,
    year: endTime ? new Date(endTime).getFullYear() : undefined,
    month: endTime ? new Date(endTime).getMonth() + 1 : undefined,
    latestReadingStartTime: startTime,
    latestReadingEndTime: endTime,
    readingStatus: status,
    latestReading: filter.sorting,
    parentClinicOption:
      filter.parentClinicOption && filter.clinicGuid
        ? filter.parentClinicOption
        : undefined,
    timezone: filter.timezone
      ? mainlandTimezonesMapping[filter.timezone]
      : undefined,
    // Strip the Multi-State License sentinel before sending; the covered states are
    // already present in the selection, so the backend only sees concrete state names.
    patientState: filter.patientState?.includes(States.MultiStateLicense)
      ? Array.from(
          new Set([
            ...filter.patientState.filter(
              (state) => state !== States.MultiStateLicense,
            ),
            ...multiStateLicenseStates,
          ]),
        )
      : filter.patientState,
  };

  const queryKey = getRpmOverviewDashboardQueryKey(filterWithItems);

  const { data, isLoading, isFetching, error } = useQuery<
    RpmOverviewDashboardResponse | undefined,
    string | undefined
  >({
    queryKey,
    queryFn: async () => queryFn(filterWithItems),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  useUpdateAssigneeOverviewDashboard(queryKey);

  return {
    patients: data,
    isLoading,
    isFetching,
  };
};
